/**
 * 内部码 → 对客中文(1004 T10)。
 *
 * 走查(演示机,付费租户老板视角)在页面上直接看到:PARSED、GENRE、COMPANY_INTRO、BRAND_PROFILE、
 * brandValues / usageScenario、(node_code: bridal)、(K22)…词典P0、hero 镜 i2i、b-roll、sessionId、
 * 「事实边界 ×.40」、积分明细「官媒写作」。这些都是后端 / 提示词 / 代码里的词,用户读不懂。
 *
 * 本文件只放**跨 feature 通用**的两件事:
 *   ① 把混在后端文案里的内部标记剥掉({@link stripInternalMarkers});
 *   ② 判一段对客文字里还有没有内部码({@link findInternalCodeLeaks})—— 给守卫测试用,
 *      `internalCodes.guard.test.ts` 拿它扫词条与走查原文,防止这些码以后再露出来。
 * 各 feature 自己的码表(体裁、子类、字段名…)留在各自 model 里,这里不集中抄一份。
 */

/** 知识库文档解析状态(行业知识库 / 企业知识库共用后端枚举;大小写都可能出现)。 */
const PARSE_STATUS: Record<string, { label: string; ok: boolean }> = {
  PARSED: { label: "已解析", ok: true },
  READY: { label: "已解析", ok: true },
  SUCCESS: { label: "已解析", ok: true },
  // 0917 知产行业包种子写过 'done'(1013 api V31 已纠正存量),兜住同类旧种子别再显示「处理中」
  DONE: { label: "已解析", ok: true },
  PENDING: { label: "待解析", ok: false },
  PARSING: { label: "解析中", ok: false },
  PROCESSING: { label: "解析中", ok: false },
  FAILED: { label: "解析失败", ok: false },
  ERROR: { label: "解析失败", ok: false },
};

/** 解析状态 → 中文 + 是否算就绪。认不出的码一律「处理中」,不把原码给用户看。 */
export function parseStatusMeta(status: string | null | undefined): { label: string; ok: boolean } {
  const key = (status ?? "").trim().toUpperCase();
  return PARSE_STATUS[key] ?? { label: key ? "处理中" : "—", ok: false };
}

/**
 * 积分明细账单名的对客改名。后端 `xb_price_sku.display_name` 里有内部叫法:
 * `reporter.article` = 「官媒写作」(新闻舱写稿的扣费名;舱已改名「新闻舱」,对客叫「新闻稿」)。
 * ★根治在 api(改 display_name);console 先按 skuCode / 原名两头兜住,api 改完这里自然不再命中。
 */
const SKU_CODE_DISPLAY: Record<string, string> = {
  "reporter.article": "新闻稿",
};
const SKU_NAME_RENAME: Record<string, string> = {
  官媒写作: "新闻稿",
};

/**
 * 账单名对客显示:先按 skuCode 钉名,再把内部叫法改成用户认得的名字;
 * 没有账单名时**不**回落裸 skuCode(`reporter.article` 这种码用户读不懂),返回 null 让该行只显示模块名。
 */
export function skuDisplayName(
  name: string | null | undefined,
  code?: string | null
): string | null {
  if (code && SKU_CODE_DISPLAY[code]) return SKU_CODE_DISPLAY[code];
  const t = (name ?? "").trim();
  if (!t) return null;
  return SKU_NAME_RENAME[t] ?? t;
}

/**
 * 剥掉混进后端文案的内部标记:
 *   ·「(K22)」「(K17、K18、K19)」—— 获客场景编号;
 *   ·「,词典P0」「词典 P1」—— 行业信号词典的优先级;
 *   ·「(node_code: bridal)」「node_code: gold」—— 产品树节点码;
 * 标记前后多出来的标点一并收拾。
 */
export function stripInternalMarkers(text: string | null | undefined): string {
  return (text ?? "")
    .replace(/[((]\s*node_code\s*[::]\s*[\w-]+\s*[))]/gi, "")
    .replace(/node_code\s*[::]\s*[\w-]+/gi, "")
    .replace(/[((]\s*K\d+(?:\s*[、,,]\s*K\d+)*\s*[))]/g, "")
    .replace(/[,,;;、]?\s*(?:行业)?(?:信号)?词典\s*P\d\b/g, "")
    .replace(/(?:行业)?词典标为不建议[;;,,]?/g, "")
    .replace(/[,,]\s*([。;;)」])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** 混进后端 / 模型文案的术语 → 中文(只换明确的内部词,别的不动)。 */
const TERM_REPLACEMENTS: Array<[RegExp, string]> = [
  // 1011 算法备案:敏感词拦截裸码(失败原因里偶有透传)→ 与 422 弹窗同一句
  [/\bCONTENT_SENSITIVE\b/g, "内容包含敏感信息,无法生成"],
  [/hero\s*镜/gi, "主推镜头"],
  [/\bi2i\b/gi, "参考图生成"],
  [/\bt2i\b/gi, "文字生图"],
  [/\bb-roll\b/gi, "插播画面"],
  [/\bsessionId\b/g, "会话号"],
  [/官媒写作/g, "新闻稿"],
  [/\bGENRE\b/g, "体裁"],
  [/\bPARSED\b/g, "已解析"],
  // 「事实边界 ×.40」这类评分权重:用户不需要权重,整段去掉
  [/\s*×\s*0?\.\d+/g, ""],
];

/**
 * 对客文字统一清洗:先剥内部标记({@link stripInternalMarkers}),再把内部术语换成中文。
 * 给后端 / 模型原文直出的地方用(质检提醒、失败原因、推荐理由、报错描述…)。
 */
export function humanizeInternalTerms(text: string | null | undefined): string {
  let out = stripInternalMarkers(text);
  for (const [re, to] of TERM_REPLACEMENTS) out = out.replace(re, to);
  return out;
}

/** 对客文字里不该出现的内部码(守卫测试用;命中返回命中的片段)。 */
const LEAK_PATTERNS: Array<[string, RegExp]> = [
  ["大写下划线枚举码", /\b[A-Z][A-Z0-9]+(?:_[A-Z0-9]+)+\b/],
  ["解析状态码", /\b(?:PARSED|PARSING|PENDING)\b/],
  ["体裁标签 GENRE", /\bGENRE\b/],
  ["驼峰字段名", /\b[a-z]+(?:[A-Z][a-z]+)+\b/],
  ["产品节点码", /node_code/i],
  ["获客场景编号", /[((]K\d+/],
  ["词典优先级", /词典\s*P\d/],
  ["镜头术语 hero 镜", /hero\s*镜/i],
  ["出图通路 i2i", /\bi2i\b/i],
  ["剪辑术语 b-roll", /\bb-roll\b/i],
  ["会话字段 sessionId", /sessionId/],
  ["权重小数 ×.40", /×\s*\.\d/],
  ["内部扣费名 官媒写作", /官媒写作/],
];

export function findInternalCodeLeaks(text: string): string[] {
  const out: string[] = [];
  for (const [name, re] of LEAK_PATTERNS) {
    const m = text.match(re);
    if (m) out.push(`${name}: ${m[0]}`);
  }
  return out;
}
